import { useEffect, useState, useRef } from "react";
import {
  addSectionToReport,
  fetchCompetitorReportById,
  fetchCompetitorReportDataConfigurations,
  fetchCompetitorReportList,
  updateCompetitorReportSection,
} from "../../apiService";
import { useParams, useSearchParams } from "react-router-dom";
import { useCompetitorReportStore } from "../../store";
import { LuLoaderCircle, LuPlus } from "react-icons/lu";
import { useGlobalStore } from "@/GlobalStore";
import Copilot from "@/components/common/copilot";
import { Award, ImagePlus, LayoutGrid, Sprout } from "lucide-react";
import ReportSection, { ReportSectionRef } from "./ReportSection";
import ReportDateSelector from "./ReportDateSelector";
import { Separator } from "@/components/ui/separator";
import { LabelDescriptionDialog } from "@/screens/creative-label/components/LabelDescriptionDialog";
import { Button } from "@/components/ui/button";
import { CreativeReportSection } from "@/screens/creatives/types";
import {
  CreativeSectionConfiguration,
  CreativeReportMetadata,
  ReportDate,
} from "@/types";
import { DataConfigurations } from "@/types";
import useStrictNavigation from "@/hooks/useStrictNavigation";
import { buildRoute } from "@/router/constants";
import PageLoader from "@/components/common/loaders/PageLoader";
import { RiChatSmileAiLine } from "@remixicon/react";
import ExpiredTokenGuard from "@/router/guards/ExpiredTokenGuard";

const waitForScrollToSettle = (
  container: HTMLElement,
  onDone: () => void,
  maxUnchangedFrames = 10,
) => {
  let lastScrollTop = container.scrollTop;
  let unchangedFrames = 0;
  const check = () => {
    const currentScrollTop = container.scrollTop;
    if (currentScrollTop === lastScrollTop) {
      unchangedFrames++;
      if (unchangedFrames >= maxUnchangedFrames) {
        onDone();
        return;
      }
    } else {
      unchangedFrames = 0;
      lastScrollTop = currentScrollTop;
    }
    requestAnimationFrame(check);
  };
  requestAnimationFrame(check);
};

const CompetitorReport = () => {
  const { reportId } = useParams();
  const {
    setReportList,
    setLoadingReportList,
    setSelectedReportItem,
    setSelectedReport,
    setSelectedReportSection,
    setDataConfiguration,
    setIsReportLoading,
    setIsAutoSaving,
    resetState,
    selectedReportItem,
    selectedReport,
    isAutoSaving,
    isReportLoading,
    loadingReportList,
    selectedReportSection,
    reportList,
    dataConfiguration,
  } = useCompetitorReportStore();

  const strictNavigate = useStrictNavigation();
  const { selectedAccount, sidebarCollapsed, allowedDateRange } = useGlobalStore();
  const sectionRefs = useRef<{ [key: string]: ReportSectionRef | null }>({});
  const sectionsContainerRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScrollRef = useRef(false);
  const [isAddingSection, setIsAddingSection] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      resetState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount?.id]);

  const getCompetitorReportList = async () => {
    if (!selectedAccount?.id) return;
    try {
      setIsReportLoading(true);
      setLoadingReportList(true);
      const fetchedReportList = (await fetchCompetitorReportList(selectedAccount?.id))
        .reports;
      setReportList(fetchedReportList);
      const successfulReports = fetchedReportList.filter(
        (report) => report.sync_status === "success",
      );
      if (reportId) {
        const report = await fetchCompetitorReportById(selectedAccount?.id, reportId);
        setSelectedReport(report);
        if (report.sections?.[0]) setSelectedReportSection(report.sections[0]);
        const reportItem = fetchedReportList.find((r) => r.id === reportId);
        if (reportItem) setSelectedReportItem(reportItem);
      } else if (successfulReports?.[0]?.id) {
        const report = await fetchCompetitorReportById(
          selectedAccount?.id,
          successfulReports[0]?.id,
        );
        setSelectedReport(report);
        if (report.sections?.[0]) setSelectedReportSection(report.sections[0]);
        setSelectedReportItem(successfulReports[0]);
        strictNavigate(buildRoute.competitorReport(selectedAccount.id, report.id));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingReportList(false);
      setIsReportLoading(false);
    }
  };

  const getDataConfigurations = async () => {
    if (!selectedAccount?.id) return;
    try {
      const res = await fetchCompetitorReportDataConfigurations(selectedAccount?.id);
      setDataConfiguration(res.configuration);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    void getDataConfigurations();
    void getCompetitorReportList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount?.id]);

  // scrolls to selected section when section is selected from sidebar
  useEffect(() => {
    if (selectedReportSection?.overrideScroll && sectionsContainerRef.current) {
      const sectionElement = sectionsContainerRef.current.querySelector(
        `[data-section-id="${selectedReportSection.id}"]`,
      ) as HTMLElement;

      if (sectionElement) {
        isProgrammaticScrollRef.current = true;
        // Calculate the offset of the section relative to the container
        const containerTop = sectionsContainerRef.current.getBoundingClientRect().top;
        const sectionTop = sectionElement.getBoundingClientRect().top;
        const scrollOffset =
          sectionTop - containerTop + sectionsContainerRef.current.scrollTop;

        sectionsContainerRef.current.scrollTo({
          top: scrollOffset,
          behavior: "smooth",
        });
        waitForScrollToSettle(sectionsContainerRef.current, () => {
          isProgrammaticScrollRef.current = false;
        });
      }
      setSelectedReportSection({
        ...selectedReportSection,
        overrideScroll: false,
      });
    }
  }, [selectedReportSection, setSelectedReportSection]);

  // selects section based on scroll
  useEffect(() => {
    const container = sectionsContainerRef.current;
    if (!container || !selectedReport?.sections) return;
    let ticking = false;
    const handleScroll = () => {
      if (ticking || isProgrammaticScrollRef.current) return;
      ticking = true;
      requestAnimationFrame(() => {
        if (!sectionsContainerRef.current) return;

        const containerRect = sectionsContainerRef.current.getBoundingClientRect();
        let maxVisibleHeight = 0;
        let mostVisibleSection: CreativeReportSection | null = null;

        for (const section of selectedReport.sections) {
          const el = sectionsContainerRef.current.querySelector(
            `[data-section-id="${section.id}"]`,
          );
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          const visibleTop = Math.max(rect.top, containerRect.top);
          const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);
          if (visibleHeight > maxVisibleHeight) {
            maxVisibleHeight = visibleHeight;
            mostVisibleSection = section;
          }
        }
        if (mostVisibleSection && mostVisibleSection.id !== selectedReportSection?.id) {
          setSelectedReportSection(mostVisibleSection);
        }
        ticking = false;
      });
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [selectedReport, selectedReportSection?.id, setSelectedReportSection]);

  const [isChatOpen, setIsChatOpen] = useState(false);

  const handleSectionTitleUpdate = async (
    sectionId: string,
    data: { title?: string; icon?: string },
  ) => {
    if (!selectedReport || !selectedAccount?.id) return;
    setIsAutoSaving(true);
    // Store the old values for rollback
    const oldSection = selectedReport.sections.find((s) => s.id === sectionId);
    if (!oldSection) return;
    try {
      // First make the API call
      await updateCompetitorReportSection(
        selectedAccount?.id,
        selectedReport.id,
        sectionId,
        {
          ...(data.title && { title: data.title }),
          ...(data.icon && { icon: data.icon }),
        },
      );
      // If API call succeeds, update the local state
      const updatedReport = {
        ...selectedReport,
        sections: selectedReport.sections.map((item) =>
          item.id === sectionId ? { ...item, ...data } : item,
        ),
      };
      setSelectedReport(updatedReport);
      // If this section is the selected section, update that too
      if (selectedReportSection?.id === sectionId) {
        setSelectedReportSection({
          ...selectedReportSection,
          ...data,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsAutoSaving(false);
    }
  };

  // to update metadata state in frontend
  const updateMetadataState = (reportId: string, metadata?: CreativeReportMetadata) => {
    setReportList(
      reportList.map((report) =>
        report.id === reportId ? { ...report, metadata: metadata ?? {} } : report,
      ),
    );
    if (selectedReportItem?.id === reportId)
      setSelectedReportItem(
        selectedReportItem
          ? {
              ...selectedReportItem,
              metadata: metadata ?? {},
            }
          : null,
      );

    if (reportId === selectedReport?.id)
      setSelectedReport({
        ...selectedReport,
        metadata: metadata ?? {},
      });
  };

  // to update sectionConfig state in frontend
  const updateSectionConfigState = (
    reportId: string,
    sectionId: string,
    sectionConfig: CreativeSectionConfiguration,
    freshDataConfiguration?: DataConfigurations,
  ) => {
    // Use fresh data if provided, otherwise fall back to store data
    const configToUse = freshDataConfiguration || dataConfiguration;

    // handle title change on section config change
    const allGroupTitle = configToUse?.dropdowns
      ?.find((d) => d.key === "group_by")
      ?.options.map((op) => op.label.trim());

    const newEligibleTitle = configToUse?.dropdowns
      ?.find((d) => d.key === "group_by")
      ?.options.find((o) => o.key === sectionConfig.dropdowns?.group_by)?.label;

    const affectingSection = selectedReport?.sections.find((s) => s.id === sectionId);
    const currentTitle = affectingSection?.title ?? "";
    const currentGroupBy = affectingSection?.configuration.dropdowns?.group_by;

    let updateTitle = false;
    if (
      newEligibleTitle && // obviously title should be there
      (currentGroupBy === sectionConfig.dropdowns?.group_by || // case - when label get's edited
        allGroupTitle?.includes(currentTitle) || // case - to not touch custom title by user
        currentTitle === "Untitled Section") // case - initial title can change
    )
      updateTitle = true;

    if (selectedReport && selectedReport.id === reportId)
      setSelectedReport({
        ...selectedReport,
        sections: selectedReport.sections.map((s) =>
          s.id === sectionId
            ? {
                ...s,
                configuration: {
                  ...s.configuration,
                  ...sectionConfig,
                },
                ...(updateTitle && { title: newEligibleTitle }),
              }
            : s,
        ),
      });
    if (selectedReportSection && selectedReportSection.id === sectionId)
      setSelectedReportSection({
        ...selectedReportSection,
        configuration: {
          ...selectedReportSection.configuration,
          ...sectionConfig,
          ...(updateTitle && { title: newEligibleTitle }),
        },
      });
    // call api for title update
    if (updateTitle && selectedAccount?.id && selectedReport) {
      void updateCompetitorReportSection(
        selectedAccount?.id,
        selectedReport.id,
        sectionId,
        {
          title: newEligibleTitle,
        },
      );
    }
  };

  const handleDateChange = (date: ReportDate | null) => {
    if (date && selectedReport) {
      // update report metadata in fe
      updateMetadataState(selectedReport?.id, {
        ...selectedReport.metadata,
        date: date,
      });
      // Call getSectionData for all sections with the new metadata
      Object.values(sectionRefs.current).forEach((ref) => {
        if (ref) {
          void ref.handleSectionDataUpdate({
            ...selectedReport.metadata,
            date: date,
          });
        }
      });
    }
  };

  const [searchParams] = useSearchParams();

  const onCreateLabel = async (labelId: string) => {
    if (!selectedAccount?.id || !selectedReport) return;
    const res = await fetchCompetitorReportDataConfigurations(selectedAccount?.id);
    setDataConfiguration(res.configuration);
    const newLabel = res?.configuration?.dropdowns
      ?.find((d) => d.key === "group_by")
      ?.options?.find((op) => op.key === labelId);

    const sectionId = searchParams.get("sectionId");
    if (!sectionId || !newLabel) return;
    const shouldApply = searchParams.get("apply") === "true";
    if (shouldApply) {
      const sectionToApply = selectedReport?.sections.find((s) => s.id === sectionId);
      if (!sectionToApply) return;
      const _config: CreativeSectionConfiguration = {
        ...sectionToApply?.configuration,
        dropdowns: {
          ...sectionToApply?.configuration.dropdowns,
          group_by: newLabel.key,
          creative_type: newLabel?.creative_type ?? null,
        },
      };
      updateSectionConfigState(selectedReport?.id, sectionId, _config, res.configuration);
      // Call handleSectionDataUpdate for the section with new config
      const sectionRef = sectionRefs.current[sectionId];
      if (sectionRef) {
        void sectionRef.handleSectionDataUpdate(selectedReport.metadata, _config);
      }
    }
  };

  const onEditLabel = async (labelId: string, isTitleOnlyChange?: boolean) => {
    if (!selectedAccount?.id || !selectedReport) return;

    // Capture old label title before fetching new configuration
    const oldLabelTitle = dataConfiguration?.dropdowns
      ?.find((d) => d.key === "group_by")
      ?.options?.find((op) => op.key === labelId)?.label;

    const res = await fetchCompetitorReportDataConfigurations(selectedAccount?.id);
    const updatedLabel = res?.configuration?.dropdowns
      ?.find((d) => d.key === "group_by")
      ?.options?.find((op) => op.key === labelId);

    if (!updatedLabel) return;

    // Update store with new configuration after capturing old title
    setDataConfiguration(res.configuration);

    if (isTitleOnlyChange) {
      // For title-only changes, just update the label in affected sections
      const sectionsToUpdate = selectedReport?.sections.filter(
        (s) => s?.configuration?.dropdowns?.["group_by"] === labelId,
      );
      sectionsToUpdate?.forEach((_section) => {
        // Update section title if it matches the old label title
        if (_section.title === oldLabelTitle || _section.title === "Untitled Section") {
          void handleSectionTitleUpdate(_section.id, { title: updatedLabel.label });
        }
      });
    } else {
      // For non-title changes, do full data refresh
      const sectionsToRefreshData = selectedReport?.sections.filter(
        (s) => s?.configuration?.dropdowns?.["group_by"] === updatedLabel.key,
      );
      sectionsToRefreshData?.forEach((_section) => {
        const updatedConfig = {
          ..._section.configuration,
          dropdowns: {
            ..._section.configuration.dropdowns,
            creative_type: updatedLabel.creative_type ?? null,
          },
        };
        updateSectionConfigState(
          selectedReport?.id,
          _section.id,
          updatedConfig,
          res.configuration,
        );
        const sectionRef = sectionRefs.current[_section.id];
        if (sectionRef) {
          void sectionRef.handleSectionDataUpdate(selectedReport.metadata, updatedConfig);
        }
      });
    }
  };

  const handleAddSection = async () => {
    const groupByDropdown = dataConfiguration?.dropdowns?.find(
      (d) => d?.key === "group_by",
    );
    const groupByOption =
      groupByDropdown?.options?.find((o) => o?.key === groupByDropdown?.default) ??
      groupByDropdown?.options?.[0];

    const lastSectionCompetitorsRaw =
      selectedReport?.sections?.[selectedReport?.sections?.length - 1]?.configuration
        ?.dropdowns?.competitors;

    const lastSectionCompetitors: string[] = Array.isArray(lastSectionCompetitorsRaw)
      ? lastSectionCompetitorsRaw
      : typeof lastSectionCompetitorsRaw === "string"
        ? [lastSectionCompetitorsRaw]
        : ["all"];

    if (selectedAccount?.id && selectedReportItem?.id) {
      try {
        setIsAddingSection(true);
        const res = await addSectionToReport(selectedAccount?.id, selectedReportItem.id, {
          title: "Untitled Section",
          icon: "🔸",
          configuration: {
            dropdowns: {
              competitors: lastSectionCompetitors,
              group_by: groupByOption?.key ?? "ad_name",
              creative_type: groupByOption?.creative_type ?? null,
            },
          },
        });

        setSelectedReport(
          selectedReport
            ? {
                ...selectedReport,
                sections: [...selectedReport.sections, res],
              }
            : null,
        );
        setTimeout(() => {
          setSelectedReportSection({ ...res, overrideScroll: true });
        }, 500);
      } catch (error) {
        console.error(error);
      } finally {
        setIsAddingSection(false);
      }
    }
  };

  if (loadingReportList || isReportLoading)
    return (
      <ExpiredTokenGuard>
        <div className="w-full h-full flex justify-center items-center">
          <PageLoader />
        </div>
      </ExpiredTokenGuard>
    );

  return (
    <ExpiredTokenGuard>
      <div className="flex w-full h-full relative">
        <div className={`flex flex-col flex-1 min-w-0 pt-[8px]`}>
          <div className="w-full flex justify-between pl-[9px] pr-[14px]">
            <div className="flex gap-2">
              {sidebarCollapsed && <div className="h-[32px] w-[32px] shrink-0"></div>}
              {/* Date Selector */}
              <ReportDateSelector
                selectedDate={selectedReport?.metadata?.date ?? null}
                onChangeApply={handleDateChange}
                timezone={selectedAccount?.time_zone}
                first_allowed_date={allowedDateRange?.first_allowed_date}
                last_allowed_date={allowedDateRange?.last_allowed_date}
              />
            </div>
            <div className="flex justify-end gap-2 pb-2">
              {/* Chat Button */}
              {isAutoSaving && (
                <div className="flex items-center gap-[4px] mr-1">
                  <LuLoaderCircle className="w-[14px] h-[14px] animate-spin flex-shrink-0 text-[#9AA3BD]" />
                  <span className="mt-[2px] overflow-hidden text-[#9AA3BD] text-ellipsis text-[13px] font-normal leading-[19.157px] tracking-[0.26px]">
                    Auto Saving...
                  </span>
                </div>
              )}
              <div
                onClick={() =>
                  !isChatOpen && selectedReportItem?.id && setIsChatOpen(true)
                }
                className={`${isChatOpen || !selectedReportItem?.id ? "hidden" : ""}`}
              >
                <div
                  className={`h-8 flex items-center gap-[6px] px-[14px] py-[6px] squircle-smooth-[1] squircle-radius-[8px] squircle-mask rounded-[8px] bg-gradient-to-r from-[#FF4500]/90 to-[#FF5188]/90 ${
                    isChatOpen || !selectedReportItem?.id
                      ? "cursor-not-allowed opacity-0 hidden"
                      : "cursor-pointer hover:text-accent-foreground"
                  }`}
                >
                  <RiChatSmileAiLine size={16} className="flex-shrink-0 text-white" />
                  <span className="text-white text-[12px] font-medium tracking-[0.24px] relative top-[1px] whitespace-nowrap">
                    Ask AI
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* sections  */}
          <div className="flex-1 overflow-y-auto" ref={sectionsContainerRef}>
            {selectedReport?.sections.map((section) => (
              <div key={section.id}>
                <ReportSection
                  className="pl-[9px] pr-[14px]"
                  ref={(ref) => {
                    sectionRefs.current[section.id] = ref;
                  }}
                  section={section}
                  handleSectionTitleUpdate={handleSectionTitleUpdate}
                  updateSectionConfigState={updateSectionConfigState}
                />
                <Separator />
              </div>
            ))}
            <div className="py-[35px] flex justify-center">
              <Button
                variant="outline"
                className="flex items-center gap-[12px]"
                onClick={() => void handleAddSection()}
                disabled={isAddingSection}
              >
                {isAddingSection ? (
                  <LuLoaderCircle className="w-[18px] h-[18px] animate-spin flex-shrink-0 text-brand-primary-new" />
                ) : (
                  <LuPlus className="w-[18px] h-[18px] flex-shrink-0 text-brand-primary-new" />
                )}
                <span className="text-brand-primary-new text-[13px] font-normal tracking-[0.26px]">
                  Add Section
                </span>
              </Button>
            </div>
          </div>
        </div>
        {/* Chat Component */}

        <Copilot
          open={isChatOpen}
          onChatOpenChange={setIsChatOpen}
          selectedAccount={selectedAccount}
          reportId={selectedReportItem?.id}
          slides={[
            [
              {
                icon: ImagePlus,
                label: "Creative Suggestions",
                title: "Creative Suggestions",
                description:
                  "Suggest 5 new ad concept along with messaging based on competitor's winning ads.",
                badgeColor: "border-[#FF5986] text-[#FF5986]",
                onCardClick: () => {},
              },
              {
                icon: Award,
                label: "Emerging Trends",
                title: "Emerging Trends",
                description:
                  "What trends have emerged in competitor ads in the last 30 days?",
                badgeColor: "border-[#629BF9] text-[#629BF9]",
                onCardClick: () => {},
              },
              {
                icon: LayoutGrid,
                label: "Untested Format",
                title: "Untested Format",
                description:
                  "Highlight ad formats competitors use but we haven't tested.",
                badgeColor: "border-[#A768F7] text-[#A768F7]",
                onCardClick: () => {},
              },
              {
                icon: Sprout,
                label: "Messaging Angle",
                title: "Messaging Angle",
                description:
                  "What are the different messaging used by different competitors?",
                badgeColor: "border-[#33AFAF] text-[#33AFAF]",
                onCardClick: () => {},
              },
            ],
          ]}
          creativeMetaData={selectedReportItem?.metadata ?? null}
          reportConfig={selectedReport}
        />
        <LabelDescriptionDialog
          onCreate={onCreateLabel}
          onEdit={onEditLabel}
          report_type="competitor"
        />
      </div>
    </ExpiredTokenGuard>
  );
};

export default CompetitorReport;
